const Logger = require("../Logger");

const MAX_RELOADS_PER_HOUR = 3;
const RELOAD_WINDOW_MS = 60 * 60 * 1000; // 1 hour
const ERROR_RECOVERY_MS = 90 * 1000; // 1.5 minutes
const HEARTBEAT_CHECK_INTERVAL_MS = 3 * 1000; // 3 seconds
const HEARTBEAT_MISS_MS = 10 * 1000; // 10 seconds

const ScreenSharingRecoveryHandler = ({ htmlName, onReload, isScreenSharingEnabled } = {}) => {
  const factory = {};

  let lastHeartbeatAt = null;
  let heartbeatCheckInterval = null;
  let errorRecoveryTimer = null;
  let deferredReloadTimer = null;
  let pendingReloadReason = null;
  let reloadCount = 0;
  let reloadSince = null;

  const _resetReloadLimit = () => {
    reloadCount = 0;
    reloadSince = null;
  };

  const _expireReloadLimitIfNeeded = () => {
    if (reloadSince != null && Date.now() - reloadSince >= RELOAD_WINDOW_MS) {
      _resetReloadLimit();
    }
  };

  const _isReloadLimitExceeded = () => reloadCount >= MAX_RELOADS_PER_HOUR;

  const _recordReload = () => {
    if (reloadSince == null) {
      reloadSince = Date.now();
    }
    reloadCount += 1;
  };

  const _cancelErrorRecoveryTimer = () => {
    if (errorRecoveryTimer) {
      clearTimeout(errorRecoveryTimer);
      errorRecoveryTimer = null;
    }
  };

  const _cancelDeferredReloadTimer = () => {
    if (deferredReloadTimer) {
      clearTimeout(deferredReloadTimer);
      deferredReloadTimer = null;
      pendingReloadReason = null;
    }
  };

  const _requestReload = (reason) => {
    if (!isScreenSharingEnabled()) {
      return;
    }

    _expireReloadLimitIfNeeded();

    if (_isReloadLimitExceeded()) {
      _scheduleDeferredReload(reason);
      return;
    }

    _recordReload();
    _cancelErrorRecoveryTimer();
    Logger.viewerInfo("Screen sharing iframe reload", {
      reason,
      htmlName,
      lastHeartbeatAt
    });
    onReload();
    lastHeartbeatAt = null;
  };

  const _scheduleDeferredReload = (reason) => {
    pendingReloadReason = reason;

    if (deferredReloadTimer) {
      return;
    }

    const delay = Math.max(0, reloadSince + RELOAD_WINDOW_MS - Date.now());

    Logger.viewerInfo("Screen sharing iframe reload limit reached, deferring reload for the next hour", {
      htmlName,
      reloadCount,
      deferMs: delay
    });

    deferredReloadTimer = setTimeout(() => {
      deferredReloadTimer = null;
      const reason = pendingReloadReason;
      pendingReloadReason = null;
      _expireReloadLimitIfNeeded();
      _requestReload(reason);
    }, delay);
  };

  const _scheduleErrorRecovery = () => {
    _cancelErrorRecoveryTimer();
    errorRecoveryTimer = setTimeout(() => {
      errorRecoveryTimer = null;
      _requestReload("error-timeout");
    }, ERROR_RECOVERY_MS);
  };

  const _checkMissedHeartbeat = () => {
    if (!isScreenSharingEnabled() || lastHeartbeatAt == null) {
      return;
    }

    if (Date.now() - lastHeartbeatAt >= HEARTBEAT_MISS_MS) {
      _requestReload("missed-heartbeat");
    }
  };

  factory.start = () => {
    factory.stop();
    _resetReloadLimit();
    lastHeartbeatAt = null;
    heartbeatCheckInterval = setInterval(_checkMissedHeartbeat, HEARTBEAT_CHECK_INTERVAL_MS);
  };

  factory.stop = () => {
    if (heartbeatCheckInterval) {
      clearInterval(heartbeatCheckInterval);
      heartbeatCheckInterval = null;
    }
    _cancelErrorRecoveryTimer();
    _cancelDeferredReloadTimer();
  };

  factory.recordHeartbeat = () => {
    lastHeartbeatAt = Date.now();
  };

  factory.onError = () => {
    _scheduleErrorRecovery();
  };

  factory.onRecovered = () => {
    _cancelErrorRecoveryTimer();
    _cancelDeferredReloadTimer();
  };

  return factory;
};

module.exports = ScreenSharingRecoveryHandler;
